import React, {
  useCallback,
  useState,
  useEffect,
  Fragment,
  forwardRef,
  useImperativeHandle,
  useRef
} from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as ButtonMui,
  TextField,
  Tooltip,
  IconButton,
  Switch,
  FormControl,
  FormGroup,
  FormControlLabel,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  InputLabel,
  Select
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Alert from '@mui/lab/Alert';
import styled from 'styled-components';
import { Text } from 'shared/components';
import HelpIcon from '@mui/icons-material/Help';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { withStyles } from '@mui/styles';
import {
  useMyEmailTemplates,
  useNewEmailTemplate,
  useDeleteEmailTemplate
} from 'shared/apollo';
import Editor from 'shared/components/Editor';
import { ErrorChip } from '../NewDonation/styles';

const TOMORROW = moment()
  .add(1, 'days')
  .format('YYYY-DD-MM');

const StyledMenu = withStyles({
  paper: {
    border: '1px solid #d3d4d5'
  }
})(props => (
  <Menu
    elevation={0}
    getContentAnchorEl={null}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'center'
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'center'
    }}
    {...props}
  />
));

const variables = [
  {
    key: 'investmentFirstName',
    help: 'Donor\'s First Name.'
  },
  {
    key: 'accountName',
    help: ''
  },
  {
    key: 'investmentRecipientOrganization',
    help: 'Name of the organization receiving the donation/contribution.'
  },
  {
    key: 'investmentFundedProgramName',
    help: ''
  },
  {
    key: 'investmentAmount',
    help: 'The amount being contributed by the donor.'
  },
  {
    key: 'userEmail',
    help: ''
  },
  { key: 'submitForFeedback', help: 'The due date for submit for feedback.' },
  { key: 'resolveFeedback', help: 'The due date for resolve feedback.' },
  {
    key: 'investmentRecipientEmail',
    help: 'The email of the non profit organizartion recipient'
  }
];

const loginUrl = window.origin + '/login?email=[investmentRecipientEmail]';

const InvitationPopup = forwardRef(
  (
    {
      open,
      onClose,
      setField,
      values,
      errors,
      onSubmit,
      investmentRecipientFirstName,
      investmentRecipientOrganization,
      portfolioYear,
      portfolioName,
      investmentAmount,
      investmentRecipientLastName,
      userFirst,
      userLast,
      userEmail,
      userPhone,
      accountName,
      npoRecipients,
      investmentFundedProgramName,
      submitForFeedback,
      resolveFeedback,
      finalizeReportDate,
      invitationSent,
      errorOnInvitation
    },
    ref
  ) => {
    const editorRef = useRef();
    const { automatic, datetime, message, email } = values;
    const {
      data: emailTemplates,
      refresh: refreshTemplates
    } = useMyEmailTemplates();
    const { run: runNew } = useNewEmailTemplate();
    const { run: runDelete } = useDeleteEmailTemplate();

    const [templateTouched, setTemplateTouched] = useState(false);
    const [asNewTemplate, setAsNewTemplate] = useState(false);
    const [templateName, setTemplateName] = useState(false);
    const [templateNameError, setTemplateNameError] = useState();
    const [messageText, setMessageText] = useState(message || '');
    const [anchorTemplatesMenu, setAnchorTemplatesMenu] = useState(null);
    const [started, setStated] = useState(false);
    const [scheduleTime, setScheduleTime] = useState();
    const [localError, setLocalError] = useState();
    const [selected, setSelected] = useState({
      uninvited: [],
      unallocated: [],
      managers: []
    });
    const keyOf = recipient =>
      `${recipient.investmentId}-${recipient.email}-${recipient.program}-${recipient.year}`;
    const isAllSelected = category =>
      npoRecipients?.[category]?.length > 0 &&
      selected[category].length === npoRecipients[category].length;
    const isSomeSelected = category =>
      selected[category].length > 0 && !isAllSelected(category);
    const toggleCategory = category => {
      setSelected(prev => ({
        ...prev,
        [category]: isAllSelected(category)
          ? []
          : npoRecipients[category].map(keyOf)
      }));
    };
    const toggleRecipient = (category, key) => {
      setSelected(prev => {
        const current = prev[category];
        return {
          ...prev,
          [category]: current.includes(key)
            ? current.filter(k => k !== key)
            : [...current, key]
        };
      });
    };
    const [date, setDate] = useState(TOMORROW);
    const [time, setTime] = useState('12:00');
    const [dtError, setDtError] = useState('');
    const [body, setBody] = useState('');
    const [currentVar, setCurrentVar] = useState('');
    const [customEditor, setCustomEditor] = useState(false);

    useEffect(() => {
      if (npoRecipients?.uninvited?.length > 0) {
        setSelected(prev => ({
          ...prev,
          uninvited: npoRecipients.uninvited.map(keyOf)
        }));
      }
    }, [npoRecipients]);

    const parseText = text => {
      const replaced = text
        .replace(
          /\[investmentRecipientFirstName\]/g,
          investmentRecipientFirstName
        )
        .replace(
          /\[investmentRecipientLastName\]/g,
          investmentRecipientLastName
        )
        .replace(/\[portfolioYear\]/g, portfolioYear)
        .replace(/\[portfolioName\]/g, portfolioName)
        .replace(/\[investmentRecipientEmail\]/g, email)
        .replace(/\[investmentAmount\]/g, investmentAmount)
        .replace(/\[userFirst\]/g, userFirst)
        .replace(/\[userLast\]/g, userLast)
        .replace(/\[userEmail\]/g, userEmail)
        .replace(/\[userPhone\]/g, userPhone)
        .replace(/\[accountName\]/g, accountName)
        .replace(
          /\[investmentRecipientOrganization\]/g,
          investmentRecipientOrganization
        )
        .replace(
          /\[investmentFundedProgramName\]/g,
          investmentFundedProgramName
        )
        .replace(/\[submitForFeedback\]/g, submitForFeedback)
        .replace(/\[resolveFeedback\]/g, resolveFeedback)
        .replace(/\[finalizeReportDate\]/g, finalizeReportDate);
      return replaced;
    };

    useEffect(() => {
      if (emailTemplates?.[0]?.body && !started) {
        const defaultTemplate = (emailTemplates ?? []).filter(
          tmp => tmp.name === 'default_invitation'
        )?.[0];
        let message = defaultTemplate?.body;
        if (defaultTemplate?.name === 'default_invitation') {
          message = message.replace('http://www.trueimpact.com/IR.', loginUrl);
        }
        setMessageText(message);
        setStated(true);
        setTemplateTouched(false);
        setField('message', parseText(defaultTemplate?.body));
        setBody(defaultTemplate?.body);
      }
    }, [emailTemplates]);

    const handleOnSubmit = useCallback(() => {
      // Invitation Already sent
      if (invitationSent) {
        setLocalError('The invitation message has already been sent.');
        return;
      }
      setLocalError();
      if (automatic) {
        if (!datetime) {
          setDtError('Invalid date and time');
          return;
        }
        if (datetime && moment(datetime) < moment().add(1, 'minutes')) {
          setDtError('Date and time should be 10 minutes from now at least');
          return;
        }
      }
      setDtError('');
      setField('message', parseText(messageText));

      const selectedRecipients = {
        uninvited: (npoRecipients?.uninvited || []).filter(r =>
          selected.uninvited.includes(keyOf(r))
        ),
        unallocated: (npoRecipients?.unallocated || []).filter(r =>
          selected.unallocated.includes(keyOf(r))
        ),
        managers: (npoRecipients?.managers || []).filter(r =>
          selected.managers.includes(keyOf(r))
        )
      };

      onSubmit(messageText, scheduleTime, selectedRecipients);
    }, [
      automatic,
      datetime,
      messageText,
      scheduleTime,
      selected,
      npoRecipients,
      setDtError,
      setField,
      parseText,
      onSubmit,
      invitationSent
    ]);

    useImperativeHandle(ref, () => ({
      runInvite() {
        handleOnSubmit();
      }
    }));

    const openTemplatesMenu = event => {
      setAnchorTemplatesMenu(event.currentTarget);
    };

    const closeTemplatesMenu = () => {
      setAnchorTemplatesMenu(null);
    };

    const handleSelectEmailTemplate = text => {
      setMessageText(text);
      setTemplateTouched(false);
      setBody(text);
      const editorFn = editorRef?.current?.updateBody;
      editorFn(text);
      closeTemplatesMenu();
    };

    const onSavetemplate = () => {
      if (!templateName) {
        setTemplateNameError('A name is required');
        return;
      }
      runNew(
        {
          name: templateName,
          body: parseText(messageText),
          subject: 'Invitation to Social Impact Reporting with True Impact'
        },
        () => {
          setTimeout(() => {
            refreshTemplates();
          }, 100);
        }
      );
      setTemplateTouched(false);
      setAsNewTemplate(false);
      setTemplateNameError();
      setTemplateName('');
    };

    const handleDeleteTemplate = id => {
      runDelete({ id }, refreshTemplates);
      closeTemplatesMenu();
    };

    const handleAutomaticChange = useCallback(() => {
      if (automatic) {
        setScheduleTime();
      }
      setField('automatic', !automatic);
    }, [automatic]);

    const handleDate = e => {
      setDate(e.target.value);
      if (time) {
        const dt = `${e.target.value}T${time}`;
        setField('datetime', dt);
        setScheduleTime(dt);
      }
    };

    const handleTime = e => {
      setTime(e.target.value);
      if (date) {
        const dt = `${date}T${e.target.value}`;
        setField('datetime', dt);
        setScheduleTime(dt);
      }
    };

    const handleAddVariable = () => {
      if (!currentVar) return;
      const editorPushVar = editorRef?.current?.pushVar;
      const editorUpdateBody = editorRef?.current?.updateBody;

      if (editorPushVar) {
        editorPushVar(`[${currentVar}]`);
      } else if (editorUpdateBody) {
        const newBody = body.trim() + ` [${currentVar}]`;
        editorUpdateBody(newBody);
      }

      setTemplateTouched(true);
      setCurrentVar('');
    };

    const handleChangeVar = event => {
      setCurrentVar(event.target.value);
    };

    const handleChangeMessage = text => {
      setTemplateTouched(true);
      setMessageText(text);
      setField('message', parseText(text));
      setBody(text);
    };

    const handleEditorChange = htmlText => {
      setTemplateTouched(true);
      setBody(htmlText);
      setMessageText(htmlText);
      setField('message', parseText(htmlText));
    };

    return (
      <Dialog onClose={onClose} open={open} maxWidth="md" fullWidth>
        <DialogTitle id="customized-dialog-title" onClose={onClose}>
          Invitation Message
          <Button
            color="primary"
            onClick={openTemplatesMenu}
            style={{ float: 'right' }}
          >
            Select Template
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          <DialogTitle
            id="customized-dialog-title"
            onClose={onClose}
            style={{ paddingLeft: 0 }}
          >
            Edit your template
          </DialogTitle>
          <Sp />
          <FormControlLabel
            control={
              <Switch
                checked={customEditor}
                onChange={() => setCustomEditor(!customEditor)}
              />
            }
            label={
              customEditor
                ? 'Custom Tempalte Editor (HTML)'
                : 'Rich Text Editor'
            }
          />
          <p>
            <span style={{ fontSize: 12 }}>
              {customEditor
                ? 'Utilize the Custom Template Editor to edit your template`s HTML fully.'
                : 'Use the rich text editor to easily customize and enhance your template`s branding.'}
            </span>
          </p>
          <Br />
          {!customEditor && (
            <VarsContainer>
              <FormControl variant="outlined">
                <InputLabel id="variables-label">Add variable</InputLabel>
                <Select
                  labelId="variables-label"
                  id="variables-label-select"
                  value={currentVar}
                  onChange={handleChangeVar}
                  label="Add variable"
                >
                  <MenuItem value="">
                    <em>Choose one...</em>
                  </MenuItem>
                  {variables.map(({ key }) => (
                    <MenuItem key={key} value={key}>
                      {' '}
                      {key}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                disabled={currentVar === ''}
                onClick={handleAddVariable}
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </VarsContainer>
          )}
          <EditorContainer>
            {!customEditor ? (
              <Editor
                onChange={handleEditorChange}
                initialValue={body}
                ref={editorRef}
              />
            ) : (
              <StyledTextField
                value={messageText}
                onChange={e => handleChangeMessage(e.target.value)}
                label="Custom message Content"
                multiline
                rows={10}
                variant="outlined"
                size="small"
                fullWidth
                error={errors.message}
                helperText={errors.message}
              />
            )}
          </EditorContainer>
          <Br />
          <p>
            <DialogTitle
              id="customized-dialog-title"
              onClose={onClose}
              style={{ paddingLeft: 0 }}
            >
              Message Preview
            </DialogTitle>
            <Preview
              dangerouslySetInnerHTML={{
                __html: parseText(
                  messageText || 'Write a message to see a preview...'
                )
              }}
            />
          </p>
          {npoRecipients && (
            <p>
              <DialogTitle onClose={onClose} style={{ paddingLeft: 0 }}>
                Who do you want to notify?
              </DialogTitle>
              <FormControl component="fieldset">
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isAllSelected('uninvited')}
                        indeterminate={isSomeSelected('uninvited')}
                        onChange={() => toggleCategory('uninvited')}
                        name="uninvited"
                        disabled={npoRecipients.uninvited.length === 0}
                      />
                    }
                    label={`All un-invited investment recipients (${npoRecipients.uninvited.length})`}
                  />
                  {npoRecipients.uninvited.length > 0 && (
                    <RecipientList>
                      {npoRecipients.uninvited.map(recipient => {
                        const key = keyOf(recipient);
                        return (
                          <RecipientRow key={key}>
                            <Checkbox
                              size="small"
                              checked={selected.uninvited.includes(key)}
                              onChange={() =>
                                toggleRecipient('uninvited', key)
                              }
                              sx={{
                                padding: '2px',
                                marginRight: '6px'
                              }}
                            />
                            <span className="recipient-text">
                              {recipient.email} (
                              {recipient.program || 'program'} -{' '}
                              {recipient.year || '-'})
                            </span>
                          </RecipientRow>
                        );
                      })}
                    </RecipientList>
                  )}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isAllSelected('unallocated')}
                        indeterminate={isSomeSelected('unallocated')}
                        onChange={() => toggleCategory('unallocated')}
                        name="unallocated"
                        disabled={npoRecipients.unallocated.length === 0}
                      />
                    }
                    label={`All invited, but unallocated investment recipients (${npoRecipients.unallocated.length})`}
                  />
                  {npoRecipients.unallocated.length > 0 && (
                    <RecipientList>
                      {npoRecipients.unallocated.map(recipient => {
                        const key = keyOf(recipient);
                        return (
                          <RecipientRow key={key}>
                            <Checkbox
                              size="small"
                              checked={selected.unallocated.includes(key)}
                              onChange={() =>
                                toggleRecipient('unallocated', key)
                              }
                              sx={{
                                padding: '2px',
                                marginRight: '6px'
                              }}
                            />
                            <span className="recipient-text">
                              {recipient.email} (
                              {recipient.program || 'program'} -{' '}
                              {recipient.year || '-'})
                            </span>
                          </RecipientRow>
                        );
                      })}
                    </RecipientList>
                  )}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isAllSelected('managers')}
                        indeterminate={isSomeSelected('managers')}
                        onChange={() => toggleCategory('managers')}
                        name="managers"
                        disabled={npoRecipients.managers.length === 0}
                      />
                    }
                    label={`NPO managers of allocated reports (${npoRecipients.managers.length})`}
                  />
                  {npoRecipients.managers.length > 0 && (
                    <RecipientList>
                      {npoRecipients.managers.map(recipient => {
                        const key = keyOf(recipient);
                        return (
                          <RecipientRow key={key}>
                            <Checkbox
                              size="small"
                              checked={selected.managers.includes(key)}
                              onChange={() =>
                                toggleRecipient('managers', key)
                              }
                              sx={{
                                padding: '2px',
                                marginRight: '6px'
                              }}
                            />
                            <span className="recipient-text">
                              {recipient.email} (
                              {recipient.program || 'program'} -{' '}
                              {recipient.year || '-'})
                            </span>
                          </RecipientRow>
                        );
                      })}
                    </RecipientList>
                  )}
                </FormGroup>
              </FormControl>
            </p>
          )}
          {(errors.firstName ||
            errors.lastName ||
            errors.email ||
            errors.organization) && (
            <Alert severity="error">
              {errors.firstName ||
                errors.lastName ||
                errors.email ||
                errors.organization}
            </Alert>
          )}
        </DialogContent>
        {(localError || errorOnInvitation) && (
          <ErrorBox>
            <ErrorChip>
              <span>{localError || errorOnInvitation}</span>
              <ErrorOutlineIcon />
            </ErrorChip>
          </ErrorBox>
        )}
        <DialogActions>
          {templateTouched && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={asNewTemplate}
                  onChange={e => setAsNewTemplate(e.target.checked)}
                  name="checkedA"
                />
              }
              label="Save as new template"
            />
          )}
          {templateTouched && asNewTemplate && (
            <TextField
              values={templateName}
              onChange={e => setTemplateName(e.target.value)}
              error={Boolean(templateNameError)}
              placeholder="Template name"
              helperText={templateNameError}
            />
          )}
          {templateTouched && asNewTemplate && (
            <IconButton
              onClick={onSavetemplate}
              color="primary"
              component="span"
            >
              <SaveIcon />
            </IconButton>
          )}
          <FormControlLabel
            control={
              <Switch checked={automatic} onChange={handleAutomaticChange} />
            }
            label={automatic ? 'Automatic' : 'Manual'}
          />
          {automatic && (
            <DateField
              label="Date"
              values={date}
              error={dtError}
              helperText={dtError}
              type="date"
              onChange={handleDate}
            />
          )}
          {automatic && (
            <DateField
              label="Time"
              values={time}
              type="time"
              onChange={handleTime}
            />
          )}
          <Button
            autoFocus
            onClick={() => handleOnSubmit()}
            color="primary"
            disabled={
              !!npoRecipients &&
              selected.uninvited.length +
                selected.unallocated.length +
                selected.managers.length ===
                0
            }
          >
            Send
          </Button>
          <Button
            autoFocus
            onClick={onClose}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Tooltip
            title={
              <Fragment>
                <P>
                  Write and invitation message to be sent to the provided
                  recipient. You can use the following variables:
                </P>
                <p>[investmentRecipientFirstName] Recipient first Name</p>
                <p>[investmentRecipientLastName] Recipient last name</p>
                <p>[investmentAmount] total investment amount</p>
                <p>
                  [investmentRecipientOrganization] Current organization name
                </p>
                <p>[portfolioYear] Current portfolio year</p>
                <p>[portfolioName] Current portfolio name</p>
                <p>[userFirst] Your fist name</p>
                <p>[userLast] Your last name</p>
                <p>[userEmail] Yourn email</p>
                <p>[userPhone] Your phone</p>
                <p>[accountName] Your donor account name</p>
              </Fragment>
            }
          >
            <IconButton aria-label="help">
              <HelpIcon />
            </IconButton>
          </Tooltip>
        </DialogActions>
        <StyledMenu
          anchorEl={anchorTemplatesMenu}
          open={Boolean(anchorTemplatesMenu)}
          onClose={closeTemplatesMenu}
        >
          {emailTemplates.map(({ dbId, name, body, accountId }) => (
            <MenuItem key={dbId}>
              <ListItemText
                primary={
                  name === 'default_invitation' ? 'Default Invitation' : name
                }
                onClick={() => handleSelectEmailTemplate(body)}
              />
              {accountId && (
                <ListItemIcon style={{ justifyContent: 'flex-end' }}>
                  <DeleteIcon
                    fontSize="small"
                    onClick={() => handleDeleteTemplate(dbId)}
                  />
                </ListItemIcon>
              )}
            </MenuItem>
          ))}
        </StyledMenu>
      </Dialog>
    );
  }
);

const DateField = styled(TextField)`
  .MuiInputLabel-shrink {
    display: block !important;
  }
  .MuiInputLabel-marginDense {
    display: none;
  }
`;

const Sp = styled.div`
  width: 100%;
  height: 1px;
  border-bottom: solid 1px #ccc;
  margin-bottom: 10px;
`;

const Br = styled.div`
  width: 100%;
  height: 10px;
`;

const Preview = styled.div`
  white-space: pre-wrap;
  padding: 20px;
  background-color: #e6e6e6;
  font-size: 0.8rem !important;
`;

const P = styled(Text)`
  font-size: 0.8rem !important;
`;

const StyledTextField = styled(TextField)`
  div {
    textarea {
      font-size: 0.8rem !important;
    }
  }
`;

const EditorContainer = styled.div`
  width: 100%;
  overflow-x: hidden;
  .rdw-editor-main {
    border: solid 1px #bbb;
    border-radius: 5px;
    padding-left: 10px;
    padding-right: 10px;
    min-height: 250px;
    span,
    div {
      color: #000;
    }
  }
`;

const VarsContainer = styled.div`
  width: 100%;
  display: flex;
  height: auto;
  margin-bottom: 20px;
  button {
    margin-left: 10px;
  }
  .MuiInputBase-root {
    width: 200px;
  }
  #variables-label:not(.Mui-focused):not(.MuiFormLabel-filled) {
    transform: translate(10px, 10px);
  }
`;

InvitationPopup.propTypes = {
  errors: PropTypes.any,
  onSubmit: PropTypes.func,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  setField: PropTypes.func,
  values: PropTypes.any,
  investmentRecipientFirstName: PropTypes.string,
  portfolioYear: PropTypes.string,
  investmentRecipientOrganization: PropTypes.string,
  portfolioName: PropTypes.string,
  investmentAmount: PropTypes.number,
  userFirst: PropTypes.string,
  investmentRecipientLastName: PropTypes.string,
  userLast: PropTypes.string,
  userEmail: PropTypes.string,
  userPhone: PropTypes.string,
  accountName: PropTypes.string,
  npoRecipients: PropTypes.any
};

const Button = styled(ButtonMui)`
  flex: 1;
  span {
    text-transform: none !important;
    font-size: 16px;
    font-weight: 700;
  }
  max-width: 200px;
`;

const ErrorBox = styled.div`
  position: relative;
  width: 50%;
  margin-left: calc(50% - 48px);
  margin-bottom: -15px;
  margin-top: 10px;
`;

const RecipientList = styled.div`
  background-color: #eaf0f6;
  border: 1px solid #cbd6e2;
  border-radius: 6px;
  padding: 12px 16px;
  margin: 6px 8px 16px 40px;
  max-height: 160px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }
`;

const RecipientRow = styled.div`
  display: flex;
  align-items: flex-start;
  font-size: 13px;
  color: #535353;
  line-height: 1.5;
  margin: 4px 0;
  word-break: break-word;

  .MuiCheckbox-root {
    flex: 0 0 auto;
  }

  .recipient-text {
    flex: 1 1 auto;
    min-width: 0;
    text-align: left;
  }
`;

export default InvitationPopup;
